import { maybe } from '@lwtears/lwt-common-frontend/lib/@common/util/maybe-util';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import usePage from '../../../../hooks/use-page';
import { FONT_GSS_FUTURA_LIGHT } from '../../../../themes/defaultTheme';
import { Alignment } from '../../../../types';
import { shouldDisplaySinglePage } from '../../../../util/paging-util';
import LetterDot from './LetterDot';

interface PageLayoutProps {
  children: any;
  pageNumber?: number;
  pagination?: boolean;
  onePageBehavior?: boolean;
  letter?: string;
  gameLayout?: boolean;
  isSummaryPage?: boolean;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  pageNumber,
  children,
  pagination = true,
  onePageBehavior = false,
  letter,
  gameLayout,
  isSummaryPage = false,
}) => {
  const [{ currentPage, noOfPages, totalPages, showCover, zoomed, firstPage }] = usePage();

  const singlePageView =
    !isSummaryPage && shouldDisplaySinglePage(currentPage, noOfPages, totalPages, firstPage);
  const oddPage = (pageNumber || currentPage) % 2 === 1;
  const classes = useStyles({ showCover });

  const SideBlueBorder: React.FC<{ oddPage: boolean }> = ({ oddPage }) => (
    <div className={`${classes.divider} ${oddPage ? classes.rightPage : ''} `}>
      <div className={classes.blueBorder}></div>
    </div>
  );

  const PageNumber: React.FC<{ pageNumber: number }> = ({ pageNumber }) => (
    <Box className={classes.pgNumber}>{pageNumber}</Box>
  );

  const PaginationComponent: React.FC<{ pageNumber: number }> = ({ pageNumber }) => (
    <Box
      className={classes.pageNumberWrapper}
      justifyContent={
        onePageBehavior
          ? Alignment.SPACE_BETWEEN
          : oddPage
          ? Alignment.FLEX_END
          : Alignment.FLEX_START
      }
    >
      <PageNumber pageNumber={pageNumber} />
      {onePageBehavior && <PageNumber pageNumber={pageNumber + 1} />}
    </Box>
  );

  const MaybePaginationComponent = maybe(PaginationComponent);
  const MaybeLetterDot = maybe(LetterDot);
  const MaybeSideBlueBorder = maybe(SideBlueBorder);

  return (
    <div
      className={`${gameLayout ? classes.gamePageWrapper : classes.pageWrapper} ${
        oddPage ? classes.rightPage : ''
      } ${singlePageView ? classes.singlePage : ''} ${
        zoomed ? (singlePageView ? classes.singlePageZoomed : classes.doublePageZoomed) : ''
      }`}
    >
      <MaybeLetterDot
        shouldRender={!!letter}
        {...{ letter: letter, pageNumber: pageNumber || currentPage }}
      />
      <MaybeSideBlueBorder shouldRender={pagination} {...{ oddPage: oddPage }} />

      <Box className={`${classes.wrapper} ${classes.contentWrapper}`}>
        <Box className={`${classes.wrapper} ${classes.bookContentWrapper}`}>{children}</Box>

        <MaybePaginationComponent
          shouldRender={pagination}
          {...{ pageNumber: pageNumber || currentPage }}
        />
      </Box>

      <MaybeLetterDot
        shouldRender={!!letter && onePageBehavior}
        {...{ letter: letter, pageNumber: (pageNumber || currentPage) + 1 }}
      />

      <MaybeSideBlueBorder shouldRender={pagination && onePageBehavior} {...{ oddPage: true }} />
    </div>
  );
};
export default PageLayout;

const useStyles = makeStyles((theme: Theme) => ({
  pageWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '112.56vh',
    height: '76vh',
    position: 'relative',
    background: (props: any) => (props.showCover ? 'transparent' : theme.palette.background.paper),
    boxShadow: (props: any) => (props.showCover ? 'none' : theme.shadows[3]),
    justifyContent: 'flex-start',
  },
  gamePageWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100% !important',
    position: 'relative',
    background: 'transparent',
    boxShadow: 'none',
    justifyContent: 'flex-start',
  },
  singlePage: {
    width: '56.28vh',
  },
  singlePageZoomed: {
    width: '71.09vh',
    height: '96vh',
  },
  doublePageZoomed: {
    width: '142.18vh',
    height: '96vh',
  },
  divider: {
    display: 'flex',
    height: '100%',
  },
  rightPage: {
    flexDirection: 'row-reverse',
  },
  pgNumber: {
    fontFamily: FONT_GSS_FUTURA_LIGHT,
  },
  blueBorder: {
    backgroundColor: theme.palette.info.light,
    width: theme.spacing(4),
  },
  wrapper: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
  contentWrapper: {
    justifyContent: 'space-between',
    flexDirection: 'column',
  },
  pageNumberWrapper: {
    display: 'flex',
    padding: theme.spacing(7),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5),
    },
  },
  bookContentWrapper: {
    flexGrow: 1,
  },
}));
